import { InputText } from "primereact/inputtext";
import { useFormContext } from "react-hook-form";
import NumberUtil from "../utility/NumberUtil";
import { getNumberFormatConfig, usesCommaDecimal } from "../utility/service/tool-service";

/**
 * 數字格式輸入元件 FormNumberInput
 *
 * - 支援多種語系的數值表達方式（越南語使用逗號作為小數點）
 * - 支援 React Hook Form 的 register 驗證
 * - 自動處理語系格式轉換
 * ============================================================================
 * 注意事項
 * ============================================================================
 * ⚠️  重要使用要件：必須同時提供 value 和 onChange 屬性，否則無法正常輸入！
 * 1. 語系檢測：元件使用系統的 getLocale() 函數判斷語系
 * 2. 格式轉換：失去焦點時會自動轉換為標準格式
 * 3. 向後相容：元件相容原有的 FormInput 屬性
 * 4. 支援語系：越南語 (vi) 使用逗號作為小數點，其他語系使用點作為小數點
 * ============================================================================
 *
 * @param {Object} props - 元件屬性
 * @param {string|number} props.value - 輸入值
 * @param {Function} props.onChange - 值變更回調函數
 * @param {Function} props.onBlur - 失去焦點回調函數
 * @param {string} props.className - CSS 類別
 * @param {Object} props.style - 內聯樣式
 * @param {string} props.placeholder - 佔位符文字
 * @param {boolean} props.disabled - 是否禁用
 * @param {boolean} props.readOnly - 是否唯讀
 * @param {string} props.type - 輸入類型，預設為 "text"
 * @param {Object} props.inputProps - 傳遞給 InputText 的其他屬性
 * @param {string} props.labelText - 標籤文字
 * @param {string} props.labelClass - 標籤 CSS 類別
 * @param {boolean} props.withLabel - 是否顯示標籤
 */

const InternationalNumberInput = ({
  value = "",
  onChange,
  onBlur,
  onInput,
  onFocus,
  onKeyDown,
  enterClick,
  className = "",
  style = {},
  placeholder = "",
  disabled = false,
  readOnly = false,
  type = "text",
  step,
  maxLength,
  autoComplete,
  inputProps = {},
  wrapperClass = "",
  wrapperStyle = {},
  register,
  errors,
  id,
  name,
  rules,
  trigger,
  labelText,
  labelClass,
  withLabel = true,
  isIncludeNegative = false,
  ...otherProps
}) => {
  // 獲取當前語系的數值格式配置
  const numberFormatConfig = getNumberFormatConfig();
  const usesCommaDecimalLocale = usesCommaDecimal();

  // 獲取 React Hook Form 的上下文（如果有的話）
  const formContext = useFormContext();
  const { watch, setValue } = formContext || {};

  // 新增：複雜欄位驗證邏輯（與 FormInput 一致）
  let errorObj = undefined;
  if (id && id.split(".").length > 1 && errors && Object.keys(errors).length > 0) {
    let tarErrors = errors;
    let errorObjCheckIdx = 0;
    const idArr = id.split(".");

    idArr.forEach((res, idx) => {
      if (tarErrors[res]) {
        errorObjCheckIdx = idx;
        tarErrors = tarErrors[res];
      }
    });

    if (errorObjCheckIdx === idArr.length - 1) {
      errorObj = tarErrors;
    } else errorObj = undefined;
  }

  /**
   * 清理小數點輸入，確保只有一個小數點
   * @param {string} inputValue - 輸入值
   * @returns {string} 清理後的值
   */
  const sanitizeDecimalInput = (inputValue) => {
    let filteredValue = inputValue;

    // 移除不需要的符號
    if (usesCommaDecimalLocale) {
      // 移除所有點號
      filteredValue = filteredValue.replace(/\./g, "");
      // 分割逗號
      const parts = filteredValue.split(",");
      if (parts.length > 2) {
        filteredValue = parts[0] + "," + parts.slice(1).join("");
      }
    } else {
      // 移除所有逗號
      filteredValue = filteredValue.replace(/,/g, "");
      // 分割點號
      const parts = filteredValue.split(".");
      if (parts.length > 2) {
        filteredValue = parts[0] + "." + parts.slice(1).join("");
      }
    }

    // 處理負號
    if (!isIncludeNegative) {
      filteredValue = filteredValue.replace(/-/g, "");
    } else {
      // 如果允許負號，確保負號只出現在最前面
      const negativeCount = (filteredValue.match(/-/g) || []).length;
      if (negativeCount > 1) {
        filteredValue = filteredValue.replace(/-/g, "");
        filteredValue = "-" + filteredValue;
      } else if (negativeCount === 1 && filteredValue[0] !== "-") {
        filteredValue = filteredValue.replace(/-/g, "");
        filteredValue = "-" + filteredValue;
      }
    }

    return filteredValue;
  };

  /**
   * 驗證數字格式是否正確
   * @param {string} inputValue - 輸入值
   * @returns {boolean} 格式是否正確
   */
  const validateNumberFormat = (inputValue) => {
    if (!inputValue) return true;

    const decimalSeparator = usesCommaDecimalLocale ? "," : ".";
    const signPart = isIncludeNegative ? "-?" : ""; // 是否允許負號

    const pattern = new RegExp(`^${signPart}[0-9]+(\\${decimalSeparator}[0-9]*)?$`);

    return pattern.test(inputValue);
  };

  /**
   * 處理鍵盤按下事件，允許所有按鍵但過濾輸入內容
   * @param {Event} e - 鍵盤事件
   */
  const handleKeyDown = (e) => {
    // 允許所有按鍵，包括 Ctrl 組合鍵
    // 實際的字符過濾在 onInput 中處理
    return;
  };

  /**
   * 處理輸入值變更
   * @param {Event} e - 輸入事件
   */
  const handleChange = (e) => {
    let inputValue = e.target.value;

    // 只允許數字、點號和逗號
    inputValue = inputValue.replace(
      new RegExp(`[^0-9${usesCommaDecimalLocale ? "," : "."}${isIncludeNegative ? "-" : ""}]`, "g"),
      ""
    );

    // 清理小數點，確保只有一個
    inputValue = sanitizeDecimalInput(inputValue);

    // 驗證格式 - 只有通過驗證的值才會被接受
    if (validateNumberFormat(inputValue)) {
      // 觸發 onChange 事件
      if (onChange) {
        onChange(inputValue);
      }

      // 觸發 onInput 事件（保持與 FormInput 相容）
      if (onInput) {
        const simulatedEvent = {
          ...e,
          target: {
            ...e.target,
            value: inputValue,
          },
        };
        onInput(simulatedEvent);
      }

      // 更新輸入框的值為清理後的有效值
      e.target.value = inputValue;

      // 如果有 register 和 trigger，觸發驗證
      if (register && trigger && (name || id)) {
        trigger(name || id);
      }
    } else {
      // 如果驗證失敗，恢復到上一個有效值或清空
      e.target.value = inputValue.replace(`/[^0-9.,${isIncludeNegative ? "-" : ""}]/g`, "");

      // 再次嘗試清理和驗證
      let cleanedValue = sanitizeDecimalInput(e.target.value);
      if (validateNumberFormat(cleanedValue)) {
        if (onChange) onChange(cleanedValue);
        if (onInput) {
          const simulatedEvent = {
            ...e,
            target: { ...e.target, value: cleanedValue },
          };
          onInput(simulatedEvent);
        }
        e.target.value = cleanedValue;
      } else {
        // 如果仍然無效，阻止輸入
        e.target.value = "";
        if (onChange) onChange("");
        if (onInput) {
          const simulatedEvent = {
            ...e,
            target: { ...e.target, value: "" },
          };
          onInput(simulatedEvent);
        }
      }
    }
  };

  /**
   * 處理失去焦點事件
   * @param {Event} e - 失去焦點事件
   */
  const handleBlur = (e) => {
    let inputValue = e.target.value;

    // 使用 NumberUtil 的標準格式轉換函數
    const standardValue = NumberUtil.toStandardFormat(inputValue);

    // 如果有 register，總是更新 register 的值為標準格式
    if (register) {
      // 觸發 register 的 onChange 事件，傳入標準格式的值
      const event = {
        target: {
          name: name || id,
          value: standardValue,
        },
      };
      // 手動觸發 register 的 onChange
      const { onChange: registerOnChange } = register(name || id, rules);
      if (registerOnChange) {
        registerOnChange(event);
      }
    }

    // 如果有 onChange prop，也調用它
    if (onChange) {
      onChange(standardValue);
    }

    // 呼叫原有的 onBlur 回調
    if (onBlur) {
      onBlur(e);
    }
  };

  /**
   * 格式化顯示值
   * @param {string|number} displayValue - 要顯示的值
   * @returns {string} 格式化後的值
   */
  const formatDisplayValue = (displayValue) => {
    // 如果值為空或未定義，直接返回
    if (displayValue == null || displayValue === "") {
      return "";
    }

    // 使用 NumberUtil 的語系格式轉換函數
    return NumberUtil.toLocaleFormat(displayValue);
  };

  // 如果有 register，使用 react-hook-form 的驗證
  if (register) {
    // 智能選擇：如果傳入了 value 就用傳入的，如果沒有就自動從 RHF 獲取
    const finalValue = value !== undefined ? value : watch ? watch(name || id) : "";

    // 智能選擇：如果傳入了 onChange 就用傳入的，如果沒有就自動處理
    const finalOnChange = onChange || (watch && setValue ? (val) => setValue(name || id, val) : undefined);

    return (
      <div className={`custom-input ${wrapperClass || ""}`} style={wrapperStyle}>
        {/* 標籤 */}
        {withLabel && labelText && (
          <label htmlFor={name || id} className={`form-label ${labelClass || ""}`}>
            {labelText}
          </label>
        )}
        <InputText
          {...register(name || id, rules)}
          value={formatDisplayValue(finalValue)} // 使用智能選擇的值
          onChange={(e) => {
            // 過濾輸入內容，只允許數字和語系特定的小數點符號
            let filteredValue = e.target.value;

            if (usesCommaDecimalLocale) {
              // 使用逗號小數點的語系：只允許數字和逗號
              filteredValue = filteredValue.replace(
                new RegExp(`[^0-9${numberFormatConfig.decimalSeparator}${isIncludeNegative ? "-" : ""}]`, "g"),
                ""
              );
              // 確保只有一個小數點符號
              const decimalCount = (
                filteredValue.match(
                  new RegExp(`\\${numberFormatConfig.decimalSeparator}${isIncludeNegative ? "-" : ""}`, "g")
                ) || []
              ).length;
              if (decimalCount > 1) {
                const parts = filteredValue.split(numberFormatConfig.decimalSeparator);
                filteredValue = parts[0] + numberFormatConfig.decimalSeparator + parts.slice(1).join("");
              }
            } else {
              // 使用點號小數點的語系：只允許數字和點號
              filteredValue = filteredValue.replace(
                new RegExp(`[^0-9${numberFormatConfig.decimalSeparator}${isIncludeNegative ? "-" : ""}]`, "g"),
                ""
              );
              // 計算小數點數量
              const dotCount = (filteredValue.match(/\./g) || []).length;

              // 如果超過一個小數點，就只保留第一個
              if (dotCount > 1) {
                const parts = filteredValue.split(".");
                filteredValue = parts[0] + "." + parts.slice(1).join("");
              }

              // 如果不允許負數，把負號去掉
              if (!isIncludeNegative) {
                filteredValue = filteredValue.replace(/-/g, "");
              }
            }

            // 檢查是否有 pattern 規則，如果有且使用逗號小數點的語系，進行符號轉換
            let finalValue = filteredValue;
            let transformedEvent = e;

            if (
              rules &&
              rules.pattern &&
              usesCommaDecimalLocale &&
              filteredValue.includes(numberFormatConfig.decimalSeparator)
            ) {
              // 將語系特定的小數點符號轉換為點號用於 pattern 驗證
              finalValue = filteredValue.replace(
                new RegExp(`\\${numberFormatConfig.decimalSeparator}${isIncludeNegative ? "-" : ""}`, "g"),
                "."
              );

              // 創建轉換後的事件物件
              transformedEvent = {
                ...e,
                target: {
                  ...e.target,
                  value: finalValue,
                },
              };
            }

            // 更新父組件的值（如果傳入了 onChange）
            if (onChange) {
              onChange(filteredValue);
            }

            // 自動更新 React Hook Form 的值（如果有的話）
            if (setValue) {
              setValue(name || id, filteredValue);
            }

            // 手動觸發 register 的 onChange，傳入轉換後的值（如果有的話）
            const { onChange: registerOnChange } = register(name || id, rules);
            if (registerOnChange) {
              registerOnChange(transformedEvent);
            }
          }}
          onBlur={handleBlur}
          onFocus={onFocus}
          onKeyDown={enterClick || onKeyDown || handleKeyDown} // 優先使用 enterClick，然後是 onKeyDown，最後是預設處理
          step={step}
          maxLength={maxLength}
          autoComplete={autoComplete || (type === "password" ? "new-password" : type)}
          className={`p-inputtext p-component ${className} ${
            (errors?.[name || id] && errors[name || id].type) ||
            (id && id.split(".").length > 1 && errorObj ? errorObj : false)
              ? " p-invalid"
              : ""
          }`}
          style={style}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          type="text" // 強制使用 text 類型以支援逗號輸入
          {...inputProps}
          {...otherProps}
        />
        {/* 顯示驗證錯誤訊息 - 使用 flex-column 確保垂直排列 */}
        {((errors?.[name || id] && errors[name || id].type) || errorObj) && (
          <small className="p-error pt-1">
            {(errors?.[name || id] && errors[name || id].message) || (errorObj && errorObj.message) || ""}
          </small>
        )}
      </div>
    );
  }

  // 如果沒有 register，使用原本的邏輯
  return (
    <InputText
      value={formatDisplayValue(value)}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={onFocus}
      onKeyDown={enterClick || onKeyDown || handleKeyDown} // 使用我們自定義的鍵盤事件處理
      step={step}
      maxLength={maxLength}
      autoComplete={autoComplete || (type === "password" ? "new-password" : type)}
      className={`p-inputtext p-component ${className}`}
      style={style}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      type="text" // 強制使用 text 類型以支援逗號輸入
      {...inputProps}
      {...otherProps}
    />
  );
};

export default InternationalNumberInput;
